import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatCardModule } from '@angular/material/card';
import { RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatSelectModule } from '@angular/material/select';
import { NumeralPipe } from '@pipes/numeral.pipe';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { FilterService } from '@services/filter.service';
import { BatteryIconComponent } from '@components/shared/battery-icon/battery-icon.component';
import { DirectivesModule } from 'src/app/directive/auto-complete-off/auto-complete-off.module';
import { format } from 'date-fns';
import { ExportService } from '@services/export.service';
import { TZDate } from '@date-fns/tz';
import { stringEquals } from '@utils/string.util';
import { ColoredTag, tagStyle } from '@utils/tag-color.util';

/** デバイス情報のViewModel */
export type DeviceViewModel = {
  /** デバイス名 */
  deviceName: string;
  /** シリアルナンバー(デバイスID) */
  serialNo: string;
  /** デバイス種類 */
  deviceType: string;
  /** 最終通信日時 */
  lastCommunicationAt?: string;
  /** ステータス */
  status?: string;
  /** バッテリー */
  battery?: number;
  /** 設定グループ */
  settingGroupName?: string;
  /** タグ */
  tag: string[];
  tagList?: ColoredTag[];
  /** 契約終了日 */
  contractExpiredAt: Date | null;
  /** デバイス契約状態 */
  validContractState: boolean;
  /** フライトモードのON/OFF */
  fmdOn?: boolean;
  /** フライトモードの有効/無効 */
  fmdEnabled?: boolean;
  /** 位置情報 */
  location?: {
    longitude: number;
    latitude: number;
    eventAt: string;
    address?: string;
  };
};

interface DeviceInfoListFilter {
  deviceName?: string;
  serialNo?: string;
  deviceType?: string | null;
  contractExpiredAt?: string;
  lastCommunicationAt?: string;
  status?: string;
  battery?: string;
  settingGroupName?: string;
  tag?: string;
  fmd?: 'active' | 'inactive' | null;
  sortState?: Sort;
  pageIndex?: number;
}

const pageId = 'DeviceInfoList';

@Component({
  selector: 'app-device-info-list-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTableModule,
    MatSortModule,
    MatMenuModule,
    MatCardModule,
    MatSelectModule,
    MatPaginatorModule,
    NumeralPipe,
    BatteryIconComponent,
    DirectivesModule,
  ],
  templateUrl: './device-info-list-table.component.html',
  styleUrl: './device-info-list-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceInfoListTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  public readonly tagStyle = tagStyle;
  /** カラム */
  public displayedColumns: string[] = [
    'deviceName',
    'serialNo',
    'deviceType',
    'lastCommunicationAt',
    'status',
    'battery',
    'settingGroupName',
    'tag',
  ];

  /** デバイス一覧 */
  @Input({ required: true }) public deviceList: DeviceViewModel[] = [];

  /** デバイス種類のリスト */
  @Input() public deviceTypes: string[] = [];

  /** タグの更新を通知 */
  @Output() updatedTag = new EventEmitter<DeviceViewModel[]>();

  /** 一覧情報 */
  public dataSource = new MatTableDataSource<DeviceViewModel>([]);

  /** 選択状態 */
  public selection = new SelectionModel<DeviceViewModel>(true, []);

  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;

  /** ページネーション要素 */
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** デバイス名検索キーワード */
  public deviceNameKeyword = '';

  /** シリアル番号検索キーワード */
  public serialNoKeyword = '';

  /** 最終通信日時検索キーワード */
  public lastCommunicationAtKeyword = '';

  /** 契約終了日検索キーワード */
  public contractExpiredAtKeyword = '';

  /** ステータス検索キーワード */
  public statusKeyword = '';

  /** バッテリー検索キーワード */
  public batteryKeyword = '';

  /** 設定グループ検索キーワード */
  public settingGroupKeyword = '';

  /** タグ検索キーワード */
  public tagKeyword = '';

  /** フライトモードのフィルタリング選択肢（active=fmdOn, inactive=fmdOff, null=無条件） */
  public selectedFmd: 'active' | 'inactive' | null = null;

  /** 選択されたデバイス種類 */
  public selectedDeviceType: string | null = null;

  /** 初期ソート値 */
  public initSortState: Sort = {
    active: 'lastCommunicationAt',
    direction: 'desc',
  };

  /** ソート状態 */
  private sortState: Sort | undefined;

  /** ページ番号 */
  private pageIndex: number | undefined;

  options = [
    {
      name: 'deviceName',
      checked: true,
      disabled: true,
      displayName: 'デバイス名',
    },
    {
      name: 'serialNo',
      checked: true,
      disabled: false,
      displayName: 'シリアル番号',
    },
    {
      name: 'deviceType',
      checked: true,
      disabled: false,
      displayName: 'デバイス種類',
    },
    {
      name: 'lastCommunicationAt',
      checked: true,
      disabled: false,
      displayName: '最終更新日時',
    },
    {
      name: 'status',
      checked: true,
      disabled: false,
      displayName: 'ステータス',
    },
    {
      name: 'battery',
      checked: true,
      disabled: false,
      displayName: 'バッテリー',
    },
    {
      name: 'settingGroupName',
      checked: true,
      disabled: false,
      displayName: '設定グループ',
    },
    { name: 'tag', checked: true, disabled: false, displayName: 'タグ' },
    {
      name: 'contractExpiredAt',
      checked: false,
      disabled: false,
      displayName: '契約終了日',
    },
    {
      name: 'fmd',
      checked: false,
      disabled: false,
      displayName: 'フライトモード',
    },
  ];

  /** csvのヘッダーを作成するためのマップ */
  private readonly csvColumnMap = [
    { key: 'deviceName', label: 'Device Name' },
    { key: 'serialNo', label: 'Serial Number' },
    { key: 'deviceType', label: 'Device Type' },
    { key: 'lastCommunicationAt', label: 'Last Connected' },
    { key: 'status', label: 'Operational Status' },
    { key: 'battery', label: 'Battery %' },
    { key: 'settingGroupName', label: 'Device Group' },
    { key: 'tag', label: 'Device Tag' },
    { key: 'contractExpiredAt', label: 'Contract End Date' },
    { key: 'fmd', label: 'FMD' },
  ];

  constructor(
    private filterService: FilterService<DeviceInfoListFilter, DeviceViewModel>,
    private cdr: ChangeDetectorRef,
    private exportService: ExportService,
  ) {
    this.filterService.setSessionStorageKey(pageId);
  }

  public ngOnInit(): void {
    // フィルター条件の取得と適用
    this.loadFilters();
  }

  public ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // ソート設定
    this.dataSource.sortingDataAccessor = (
      item: DeviceViewModel,
      property: string,
    ) => {
      if (property === 'fmd') {
        if (item.fmdEnabled === true && item.fmdOn === true) return 2; // Active
        if (item.fmdEnabled === true && item.fmdOn !== true) return 1; // InActive
        return 0; // 未定義
      }
      // それ以外はデフォルトの動作
      const value = item[property as keyof DeviceViewModel];

      // null/undefinedの場合は空文字
      if (value == null) return '';

      // number型の場合はそのまま返す
      if (typeof value === 'number') return value;

      // Date型の場合はタイムスタンプを返す
      if (value instanceof Date) return value.getTime();

      // それ以外は文字列化して返す
      return String(value);
    };

    // ソートとページングの変更を検知する
    this.filterService.monitorSortAndPagination(this.sort, this.paginator);

    // ソート設定
    if (this.sort && this.sortState) {
      const sortState: Sort = {
        active: this.sortState.active,
        direction: this.sortState.direction,
      };

      this.filterService.setSort(this.dataSource, this.sort, sortState);
    }

    // ページングを設定
    if (this.paginator && this.pageIndex) {
      this.setPaginationIndex(this.pageIndex);
    }

    // フィルタ設定
    this.dataSource.filterPredicate = (element: DeviceViewModel) => {
      let deviceName = true;
      if (this.deviceNameKeyword) {
        // deviceNameKeywordが空でない場合のみフィルタリングを行う
        deviceName =
          element.deviceName
            .trim()
            .toLowerCase()
            .indexOf(this.deviceNameKeyword.toLowerCase()) > -1;
      }
      let serialNo = true;
      if (this.serialNoKeyword) {
        // serialNoKeywordが空でない場合のみフィルタリングを行う
        serialNo =
          element.serialNo
            .trim()
            .toLowerCase()
            .indexOf(this.serialNoKeyword.toLowerCase()) > -1;
      }
      let deviceType = true;
      if (this.selectedDeviceType) {
        deviceType = element.deviceType === this.selectedDeviceType;
      }
      let contractExpiredAt = true;
      if (this.contractExpiredAtKeyword) {
        contractExpiredAt =
          element.contractExpiredAt === null
            ? false
            : stringEquals(
                `${element.contractExpiredAt.getFullYear()}/${String(
                  element.contractExpiredAt.getMonth() + 1,
                ).padStart(2, '0')}/${String(
                  element.contractExpiredAt.getDate(),
                ).padStart(2, '0')}`,
                this.contractExpiredAtKeyword,
              );
      }
      let lastCommunicationAt = true;
      if (this.lastCommunicationAtKeyword) {
        // lastCommunicationAtKeywordが空でない場合のみフィルタリングを行う

        // フィルタリング時に比較できるように画面上の表記形式（yyyy/MM/dd HH:mm:ss）と合わせる
        const searchTarget =
          element.lastCommunicationAt != null
            ? format(
                new Date(element.lastCommunicationAt),
                'yyyy/MM/dd HH:mm:ss',
              )
            : '';
        lastCommunicationAt =
          searchTarget
            .trim()
            .toLowerCase()
            .indexOf(this.lastCommunicationAtKeyword.toLowerCase()) > -1;
      }
      let status = true;
      if (this.statusKeyword) {
        // statusKeywordが空でない場合のみフィルタリングを行う
        status =
          (element.status ?? '')
            .trim()
            .toLowerCase()
            .indexOf(this.statusKeyword.toLowerCase()) > -1;
      }
      let battery = true;
      if (this.batteryKeyword) {
        // batteryKeywordが空でない場合のみフィルタリングを行う
        battery =
          (String(element.battery) ?? '')
            .trim()
            .toLowerCase()
            .indexOf(this.batteryKeyword.toLowerCase()) > -1;
      }
      let settingGroupName = true;
      if (this.settingGroupKeyword) {
        // settingGroupNameKeywordが空でない場合のみフィルタリングを行う
        settingGroupName =
          (element.settingGroupName ?? '')
            .trim()
            .toLowerCase()
            .indexOf(this.settingGroupKeyword.toLowerCase()) > -1;
      }
      let tag = true;
      if (this.tagKeyword) {
        // tagKeywordが空でない場合のみフィルタリングを行う
        tag = element.tag.some((tagItem) =>
          tagItem.trim().toLowerCase().includes(this.tagKeyword.toLowerCase()),
        );
        // 並べ替え処理
        if (tag) {
          const sortedTags = [...element.tag].sort((a, b) =>
            this.compareTags(a, b),
          );
          element.tag = sortedTags;
        }
      }

      let fmd = true;
      if (this.selectedFmd !== null) {
        const fmdEnabled = element.fmdEnabled === true;
        const fmdOn = element.fmdOn === true;

        switch (this.selectedFmd) {
          case 'active':
            fmd = fmdEnabled && fmdOn;
            break;
          case 'inactive':
            fmd = fmdEnabled && !fmdOn;
            break;
          default:
            fmd = true;
        }
      }

      // フィルター条件を保持
      this.saveFiltersToStorage();

      return (
        deviceName &&
        serialNo &&
        deviceType &&
        contractExpiredAt &&
        lastCommunicationAt &&
        status &&
        battery &&
        settingGroupName &&
        tag &&
        fmd
      );
    };
    this.applyFilter();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['deviceList'] !== undefined) {
      const deviceList: DeviceViewModel[] = changes['deviceList'].currentValue;
      this.dataSource.data = deviceList;
    }
  }

  /**
   * 表示するリストの項目を設定する
   */
  public updateDisplayList() {
    this.displayedColumns = this.options
      .filter((option) => option.checked)
      .map((option) => option.name);
  }

  /**
   * 検索条件（FilterPredicate）に基づいて、フィルタリング処理を適用する。
   */
  public applyFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    // （Angular materialのtable-data-source.tsの_filterDataメソッド参照）
    this.dataSource.filter = 'dummy';
  }

  /**
   * デバイス名でのフィルタリングをクリアする。
   */
  public clearDeviceNameFilter(): void {
    // フィルタ条件をクリア
    this.deviceNameKeyword = '';

    this.applyFilter();
  }

  /**
   * シリアル番号でのフィルタリングをクリアする。
   */
  public clearSerialNoFilter(): void {
    // フィルタ条件をクリア
    this.serialNoKeyword = '';

    this.applyFilter();
  }

  /**
   * 契約終了日でのフィルタリングをクリアする。
   */
  public clearContractExpiredAtFilter(): void {
    // フィルタ条件をクリア
    this.contractExpiredAtKeyword = '';

    this.applyFilter();
  }

  /**
   * 最終更新日時でのフィルタリングをクリアする。
   */
  public clearLastCommunicationAtFilter(): void {
    // フィルタ条件をクリア
    this.lastCommunicationAtKeyword = '';

    this.applyFilter();
  }

  /**
   * ステータスでのフィルタリングをクリアする。
   */
  public clearStatusFilter(): void {
    // フィルタ条件をクリア
    this.statusKeyword = '';

    this.applyFilter();
  }

  /**
   * バッテリーでのフィルタリングをクリアする。
   */
  public clearBatteryFilter(): void {
    // フィルタ条件をクリア
    this.batteryKeyword = '';

    this.applyFilter();
  }

  /**
   * 設定グループでのフィルタリングをクリアする。
   */
  public clearSettingGroupFilter(): void {
    // フィルタ条件をクリア
    this.settingGroupKeyword = '';

    this.applyFilter();
  }

  /**
   * タグでのフィルタリングをクリアする。
   */
  public clearTagFilter(): void {
    // フィルタ条件をクリア
    this.tagKeyword = '';

    this.applyFilter();
  }

  /**
   * 各フィルターを初期化する。
   */
  public clearFilters() {
    // フィルターを初期化
    this.loadFilters(true);

    const sortState: Sort = this.initSortState;

    this.filterService.setSort(this.dataSource, this.sort, sortState);

    this.setPaginationIndex();

    // フィルターを適用
    this.dataSource.filter = '';
    this.applyFilter();
  }

  /**
   * タグを比較してソートする。
   * @param a - 比較する最初のタグ
   * @param b - 比較する2番目のタグ
   * @returns
   */
  private compareTags(a: string, b: string): number {
    const aLower = a.trim().toLowerCase();
    const bLower = b.trim().toLowerCase();
    return aLower.localeCompare(bLower);
  }

  /**
   * ページネーションの番号を設定する
   */
  private setPaginationIndex(pageIndex: number = 0) {
    if (this.pageIndex) {
      setTimeout(() => {
        this.setPageIndex(pageIndex);
      }, 50);
      this.setPageIndex(pageIndex);
    }
  }

  private setPageIndex(pageIndex: number) {
    pageIndex = this.filterService.getValidPageIndex(this.paginator, pageIndex);

    this.paginator.pageIndex = pageIndex!;
    this.cdr.detectChanges();
  }

  /**
   * フィルターをセッションストレージから読み込む。
   */
  private loadFilters(reset?: boolean): void {
    const filters = this.filterService.getFiltersFromSessionStorage(reset);
    this.deviceNameKeyword = filters.deviceName || '';
    this.serialNoKeyword = filters.serialNo || '';
    this.selectedDeviceType = filters.deviceType || '';
    this.contractExpiredAtKeyword = filters.contractExpiredAt || '';
    this.lastCommunicationAtKeyword = filters.lastCommunicationAt || '';
    this.statusKeyword = filters.status || '';
    this.batteryKeyword = filters.battery || '';
    this.settingGroupKeyword = filters.settingGroupName || '';
    this.tagKeyword = filters.tag || '';
    this.selectedFmd = filters.fmd ?? null;
    this.sortState = filters.sortState;
    this.pageIndex = filters.pageIndex;
  }

  /**
   * フィルター条件をセッションストレージに保存する
   */
  private saveFiltersToStorage(): void {
    const filterList: DeviceInfoListFilter = {
      deviceName: this.deviceNameKeyword,
      serialNo: this.serialNoKeyword,
      deviceType: this.selectedDeviceType,
      contractExpiredAt: this.contractExpiredAtKeyword,
      lastCommunicationAt: this.lastCommunicationAtKeyword,
      status: this.statusKeyword,
      battery: this.batteryKeyword,
      settingGroupName: this.settingGroupKeyword,
      tag: this.tagKeyword,
      // fmd: this.selectedFmd, // リロード時にデフォルト非表示となるため、保存しない
    };
    this.filterService.setFiltersToSessionStorage(filterList);
  }

  /**
   * エクスポートボタン押下時に呼ばれる
   * - responseDeviceList を基に CSV を生成してダウンロードする
   */
  public exportDeviceList(): void {
    const csvData = (this.deviceList ?? []).map((device) => ({
      deviceName: device.deviceName ?? '',
      serialNo: device.serialNo ?? '',
      deviceType: device.deviceType ?? '',
      contractExpiredAt: device.contractExpiredAt
        ? format(
            new TZDate(device.contractExpiredAt, 'Asia/Tokyo'),
            'yyyy-MM-dd',
          )
        : '',
      lastCommunicationAt: device.lastCommunicationAt ?? '',
      status: device.status ?? '',
      battery: device.battery ?? null,
      settingGroupName: device.settingGroupName ?? '',
      tag: (device.tag ?? []).join(','),
      fmd:
        device.fmdEnabled === true
          ? device.fmdOn === true
            ? true
            : false
          : '',
    }));

    const fileName = `${format(new Date(), 'yyyyMMddHHmm')}_devices.csv`;

    // exportService の呼び出し（BOM付、日付フォーマット、入力タイムゾーンを指定）
    this.exportService.exportToCsv(
      this.csvColumnMap,
      csvData,
      fileName,
      true, // BOM付き
      "yyyy-MM-dd'T'HH:mm:ss",
      'Asia/Tokyo',
      'Asia/Tokyo',
      [
        'deviceName',
        'serialNo',
        'deviceType',
        'status',
        'settingGroupName',
        'tag',
        'contractExpiredAt',
      ],
    );
  }
}
